import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { useApolloClient, useMutation } from '@apollo/client'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from '@10xLabs/react-web/components/Row'
import { canBeDeactivated, resolvers as tripsResolvers } from 'resolvers/trips'
import { getUnprocessableErrors } from 'utils/error'
import { ReactElement } from 'react'
import { canActivateTripSale, canDeactivateTripSale } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import {
  ACTIVATE_LOCAL_TRIP_SALE,
  ACTIVATE_TRIP_SALE,
  DEACTIVATE_LOCAL_TRIP_SALE,
  DEACTIVATE_TRIP_SALE
} from 'features/Trips/mutations-legacy'
import { IToggleTripModalProps } from './types'
import { GTTripV2SaleStatus } from 'gql/types.generated'

const ModalContainer = styled(Modal.Container)`
  max-width: 600px;
`

const DateWrapper = styled.div<TSFixMe>`
  display: flex;
  margin: 3px 0;
`

export const ToggleTripModal = ({ trip, closeModal }: IToggleTripModalProps): ReactElement => {
  const intl = useIntl()
  const client = useApolloClient()
  client.addResolvers(tripsResolvers)
  const login = useLogin()

  const [activateLocalTripSale] = useMutation(ACTIVATE_LOCAL_TRIP_SALE)
  const [activateTripSale, { loading: activateLoading, error: activateError }] = useMutation(ACTIVATE_TRIP_SALE, {
    onCompleted() {
      activateLocalTripSale({
        variables: {
          tripId: trip.id
        }
      })
      closeModal()
    }
  })
  const [deactivateLocalTripSale] = useMutation(DEACTIVATE_LOCAL_TRIP_SALE)
  const [deactivateTripSale, { loading: deactivateLoading, error: deactivateError }] = useMutation(
    DEACTIVATE_TRIP_SALE,
    {
      onCompleted() {
        deactivateLocalTripSale({
          variables: {
            tripId: trip.id
          }
        })
        closeModal()
      }
    }
  )

  const loading = activateLoading || deactivateLoading
  const error = activateError || deactivateError
  const isDeactivateTrip = trip.saleStatus === GTTripV2SaleStatus.Activated

  const canToggleTripActivation =
    canBeDeactivated(trip) &&
    ((trip.saleStatus === GTTripV2SaleStatus.Deactivated && canActivateTripSale(login.accessToken.policies)) ||
      (trip.saleStatus === GTTripV2SaleStatus.Activated && canDeactivateTripSale(login.accessToken.policies)))

  const toggleSellActivation = (): void => {
    if (canToggleTripActivation) {
      if (trip.saleStatus === GTTripV2SaleStatus.Deactivated) {
        activateTripSale({
          variables: {
            tripId: trip.id
          }
        })
      } else {
        deactivateTripSale({
          variables: {
            tripId: trip.id
          }
        })
      }
    }
  }

  const handleSubmit = (event: TSFixMe): void => {
    event.preventDefault()
    toggleSellActivation()
  }

  const deactivationTitle = (
    <FormattedMessage
      id="trips.deactivation.title"
      defaultMessage="Deactivate trip {id}"
      values={{
        id: trip.id.substring(0, 8)
      }}
    />
  )

  const activationTitle = (
    <FormattedMessage
      id="trips.activation.title"
      defaultMessage="activate trip {id}"
      values={{
        id: trip.id.substring(0, 8)
      }}
    />
  )

  const deactivationConfirmationText = (
    <FormattedMessage
      id="trip.deactivation.confirmation.text"
      defaultMessage="Are you sure you want to set this trip as inactive?"
    />
  )

  const activationConfirmationText = (
    <FormattedMessage
      id="trip.activation.confirmation.text"
      defaultMessage="Are you sure you want to activate this trip?"
    />
  )

  const deactivationButtonText = <FormattedMessage id="trips.deactivation.action" defaultMessage="Deactivate Trip" />
  const activationButtonText = <FormattedMessage id="trips.activation.action" defaultMessage="Activate Trip" />
  const firstStop = trip.itinerary.at(0)
  const lastStop = trip.itinerary.at(-1)

  return (
    <ModalContainer large>
      <Modal.Header>
        <Heading.H5>{isDeactivateTrip ? deactivationTitle : activationTitle}</Heading.H5>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Content>
          <Row>
            <Column>
              <div>
                <FormattedMessage
                  id="trip.toggle.confirmation.tripDetails"
                  defaultMessage="Trip from <strong>{origin}</strong> to <strong>{destination}</strong>"
                  values={{
                    origin: firstStop?.place.name,
                    destination: lastStop?.place.name,
                    strong: (msg) => <strong>{msg}</strong>
                  }}
                />
              </div>
              <DateWrapper>
                <FormattedDate intl={intl} value={trip.departureAt} month="2-digit" day="2-digit" year="numeric" />
                &nbsp;
                <FormattedTimeByDate
                  intl={intl}
                  date={trip.departureAt}
                  timeZone={firstStop?.place.timezone || undefined}
                />
              </DateWrapper>
            </Column>
          </Row>
          <Row>
            <Column>
              <div>{isDeactivateTrip ? deactivationConfirmationText : activationConfirmationText}</div>
            </Column>
          </Row>
        </Modal.Content>
        <Modal.Footer>
          {error && (
            <Modal.ErrorMessage>
              <ErrorMessage errors={getUnprocessableErrors(error)}>
                <FormattedMessage
                  id="trips.deactivation.error"
                  defaultMessage="There was an error while deactivating the trip"
                />
              </ErrorMessage>
            </Modal.ErrorMessage>
          )}
          <Modal.Actions>
            <Button kind="cancel" onClick={(): void => closeModal()}>
              <FormattedMessage id="modal.actions.close" defaultMessage="No, close" />
            </Button>
            <Button kind="primary" loading={loading}>
              {isDeactivateTrip ? deactivationButtonText : activationButtonText}
            </Button>
          </Modal.Actions>
        </Modal.Footer>
      </form>
    </ModalContainer>
  )
}
